import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute } from '@angular/router';
import { provideTranslocoScope, TranslocoPipe } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SALES } from '@rma/generic/domain/types';
import { GoogleMapsScriptService } from '@rma/generic/maps/api';
import { RmaMapMarker, RmaMapMarkerDetail } from '@rma/generic/maps/domain';
import { CoordsToMapMarkerPipe, MapMarkerService } from '@rma/generic/maps/util';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { CurrentUser, PublicUserContextService } from '@rma/public/accounts/user';
import { BrokenImageDirective } from '@rma/public/generic/ui/broken-image';
import { LetDirective } from '@rma/public/generic/ui/let';
import { TrixelComponent, TrixelSourceCategories } from '@rma/public/generic/ui/trixels';
import { Environment, PlatformService } from '@rma/public/generic/util/environment';
import { SubscriptionFeatureService } from '@rma/public/subscriptions/public-api/da-subscriptions';
import { fromPartial } from '@total-typescript/shoehorn';
import { combineLatest, map, Observable, withLatestFrom } from 'rxjs';
import { MarketReportProfile, MarketReportPropertyDetails } from '../domain';
import { MarketReportWithQrCode } from '../domain/market-report';
import { MarketReportPublishComponent } from '../feat-market-report-publish/market-report-publish.component';
import { MarketReportComparablePropertiesComponent } from '../ui-market-report-comparable-properties/market-report-comparable-properties.component';
import { MarketReportFooterComponent } from '../ui-market-report-footer/market-report-footer.component';
import { MarketReportHeaderComponent } from '../ui-market-report-header/market-report-header.component';
import { MarketReportPrintHeaderComponent } from '../ui-market-report-print-header/market-report-print-header.component';
import { MarketReportPropertyComponent } from '../ui-market-report-property/market-report-property.component';
import { MarketReportRankingsComponent } from '../ui-market-report-rankings/market-report-rankings.component';
import { MarketReportRecognitionComponent } from '../ui-market-report-recognition/market-report-recognition.component';
import { MarketReportReviewsComponent } from '../ui-market-report-reviews/market-report-reviews.component';
import { MarketReportStatisticsComponent } from '../ui-market-report-statistics/market-report-statistics.component';

@UntilDestroy()
@Component({
  selector: 'rma-market-report',
  templateUrl: './market-report.component.html',
  styleUrls: ['./market-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    BrokenImageDirective,
    LetDirective,
    MarketReportComparablePropertiesComponent,
    MarketReportFooterComponent,
    MarketReportHeaderComponent,
    MarketReportPrintHeaderComponent,
    MarketReportPropertyComponent,
    MarketReportPublishComponent,
    MarketReportRankingsComponent,
    MarketReportRecognitionComponent,
    MarketReportReviewsComponent,
    MarketReportStatisticsComponent,
    MatButtonModule,
    TranslocoPipe,
    TrixelComponent,
  ],
  providers: [CoordsToMapMarkerPipe, MapMarkerService, provideTranslocoScope('marketReports')],
})
export class MarketReportComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly googleMapsService = inject(GoogleMapsScriptService);
  private readonly mapMarkerPipe = inject(MapMarkerService);
  private readonly coordsToMapMarkerPipe = inject(CoordsToMapMarkerPipe);
  private readonly currentUserService = inject(PublicUserContextService);
  private readonly platformService = inject(PlatformService);
  private readonly environment = inject(Environment);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly subscriptionFeatureService = inject(SubscriptionFeatureService);

  public report$: Observable<MarketReportWithQrCode> = this.route.data.pipe(map((x) => x.report));
  public isReportOwner$: Observable<boolean>;
  public isAuthorised$: Observable<boolean>;
  public isPrintMode$ = this.route.queryParams.pipe(
    map(({ print }) => !!print),
    rmaShareReplay(),
  );

  public primaryMapMarker$ = this.report$.pipe(
    withLatestFrom(this.isPrintMode$),
    map(([report, isStaticMap]) => this.mapProfileToRmaMapMarker(report.propertyDetails, isStaticMap, report.listingReportCode)),
    rmaShareReplay(),
  );

  public trixelCategory = TrixelSourceCategories.MarketReport;

  ngOnInit() {
    this.isReportOwner$ = combineLatest([this.report$, this.currentUserService.currentUser$]).pipe(
      map(([report, currentUser]) => this.getIsReportOwner(report.profile, currentUser)),
    );

    this.isAuthorised$ = this.subscriptionFeatureService.currentAccess$.pipe(map((x) => x?.hasListingReport === true));

    if (this.platformService.isPlatformBrowser) {
      this.isPrintMode$
        .pipe(untilDestroyed(this))
        .subscribe(() => this.host.nativeElement.style.setProperty('--page-size', this.environment.countryCode === 'US' ? 'letter' : 'A4'));
    }
  }

  loadGoogleMapsScript(): Observable<boolean> {
    return this.googleMapsService.appendScript();
  }

  private getIsReportOwner(reportProfile: MarketReportProfile, currentUser: CurrentUser | null): boolean {
    if (!currentUser) {
      return false;
    }

    if (reportProfile.agentCode) {
      return reportProfile.agentCode === currentUser.agent?.agentCode;
    }

    return currentUser.agencies ? currentUser.agencies?.some((x) => x.agencyCode === reportProfile.agencyCode) : false;
  }

  private mapProfileToRmaMapMarker(profile: MarketReportPropertyDetails, isStaticMap: boolean, itemId: string): RmaMapMarker | null {
    if (isStaticMap) {
      const marker = this.coordsToMapMarkerPipe.transform(
        {
          lat: profile.latitude ?? 0,
          lon: profile.longitude ?? 0,
        },
        { status: 'active', mapType: 'CompletedListing' },
      );

      return marker;
    }

    const detail: RmaMapMarkerDetail = {
      id: itemId,
      combinedHash: 0,
      title: profile.streetAddress,
      codes: [itemId],
      coords: {
        lat: profile.latitude ?? 0,
        lon: profile.longitude ?? 0,
      },
      markerCount: 1,
      status: 'standard',
      hovered: false,
      verticalType: SALES,
      info: [],
      markerType: 'CompletedListing',
    };

    const { content, zIndex, gmpClickable } = this.mapMarkerPipe.transform(fromPartial({ markerType: 'CompletedListing' }), 'primary', 0);

    const marker: RmaMapMarker = {
      detail,
      position: { lat: detail.coords.lat, lng: detail.coords.lon },
      content,
      zIndex,
      title: profile.streetAddress,
      options: {
        gmpClickable,
      },
    };

    return marker;
  }
}
